import React from 'react';
import {
    Globe, Mail, Crown, Monitor, Server,
    Eye, EyeOff, Trash2, Calendar, MoreVertical,
    Copy, Terminal
} from 'lucide-react';
// Added SortDirection to named imports
import { Table, Column, TableCell, TableRow, SortDirection } from '@/components/common/ui/Table';
import { WatchlistItem, WatchlistEntryType, WatchlistStatus } from '@/lib/api/types';
import {
    DropdownMenu,
    DropdownMenuTrigger,
    DropdownMenuContent,
    DropdownMenuItem,
    DropdownMenuSeparator
} from '@/components/common/ui/DropdownMenu';

// Defined SortField to resolve "Cannot find name" error
type SortField = 'added_at' | 'entry_type' | 'status' | 'entry';

interface WatchlistTableProps {
    items: WatchlistItem[];
    isLoading: boolean;
    selectedItems: number[];
    sortField: SortField;
    sortDirection: SortDirection;
    onSort: (column: string, direction: SortDirection) => void;
    onSelectAll: (checked: boolean) => void;
    onSelectItem: (itemId: number, checked: boolean) => void;
    onToggleItem: (item: WatchlistItem) => void;
    onDeleteItem: (id: number) => void;
    itemsPerPage?: number;
}

// --- Internal Sub-Components ---

const EntryTypeBadge: React.FC<{ entryType: WatchlistEntryType }> = ({ entryType }) => {
    const getConfig = (type: WatchlistEntryType) => {
        switch (type) {
            case 'domain': return { border: 'border-blue-500/40 text-blue-400', label: 'DOMAIN', icon: <Globe className="h-3 w-3" /> };
            case 'email': return { border: 'border-[#50A970]/40 text-[#50A970]', label: 'EMAIL', icon: <Mail className="h-3 w-3" /> };
            case 'vip': return { border: 'border-[#4da760]/40 text-[#4da760]', label: 'VIP_AUTH', icon: <Crown className="h-3 w-3" /> };
            case 'computername': return { border: 'border-orange-500/40 text-orange-400', label: 'HOST_ID', icon: <Monitor className="h-3 w-3" /> };
            case 'ip': return { border: 'border-[#FF3333]/40 text-[#FF3333]', label: 'IPV4_ADDR', icon: <Server className="h-3 w-3" /> };
            default: return { border: 'border-slate-500/40 text-slate-400', label: 'UNKNOWN', icon: <Terminal className="h-3 w-3" /> };
        }
    };

    const config = getConfig(entryType);
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border bg-black/40 text-[9px] font-bold uppercase tracking-widest ${config.border}`}>
            {config.icon}
            {config.label}
        </span>
    );
};

const StatusBadge: React.FC<{ status: WatchlistStatus }> = ({ status }) => {
    const isEnabled = status === 'enabled';
    return (
        <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded border text-[9px] font-bold uppercase tracking-widest ${isEnabled ? 'border-[#50A970]/40 text-[#50A970] bg-[#50A970]/5' : 'border-slate-600/40 text-slate-500 bg-slate-900/20'
            }`}>
            {isEnabled ? <Eye className="h-3 w-3" /> : <EyeOff className="h-3 w-3" />}
            {status}
        </span>
    );
};

const EntryDisplay: React.FC<{ entry: string; entryType: WatchlistEntryType }> = ({ entry }) => {
    const copyToClipboard = () => navigator.clipboard.writeText(entry);
    const truncateEntry = (text: string, max = 40) => text.length <= max ? text : text.substring(0, max) + '...';

    return (
        <div className="flex items-center gap-3 group min-w-0">
            <span className="font-mono text-[12px] text-slate-200 group-hover:text-[#4da760] transition-colors truncate" title={entry}>
                {truncateEntry(entry)}
            </span>
            <button
                onClick={copyToClipboard}
                className="opacity-0 group-hover:opacity-100 p-1 text-slate-500 hover:text-white transition-all outline-none"
                title="Copy Target"
            >
                <Copy className="h-3 w-3" />
            </button>
        </div>
    );
};

const DateDisplay: React.FC<{ date: string }> = ({ date }) => (
    <div className="flex items-center gap-2 text-slate-400 font-mono text-[11px] uppercase">
        <Calendar className="h-3.5 w-3.5 text-slate-600" />
        {new Date(date).toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' })}
    </div>
);

// --- Main Table Component ---

const WatchlistTable: React.FC<WatchlistTableProps> = ({
    items,
    isLoading,
    selectedItems,
    sortField,
    sortDirection,
    onSort,
    onSelectAll,
    onSelectItem,
    onToggleItem,
    onDeleteItem,
    itemsPerPage = 50
}) => {
    const columns: Column[] = [
        { id: 'entry_type', label: 'Classification', sortable: true, width: '140px' },
        { id: 'entry', label: 'Monitor_Target', sortable: true, minWidth: '250px' },
        { id: 'status', label: 'Protocol', sortable: true, width: '120px' },
        { id: 'notify_email', label: 'Alert_Node', sortable: true, width: '200px' },
        { id: 'added_at', label: 'Injected', sortable: true, width: '130px' },
        { id: 'actions', label: 'Control', width: '80px' }
    ];

    if (!isLoading && items.length === 0) {
        return (
            <div className="flex flex-col items-center justify-center py-20 bg-[#0A0B0E] border-t border-[#1A1D24] font-mono">
                <Terminal className="h-10 w-10 text-slate-700 mb-4" />
                <h3 className="text-sm font-bold text-slate-300 uppercase tracking-[0.2em]">No Watchlist Items Found.</h3>
                <p className="text-[10px] text-slate-400 mt-2 uppercase">No items match your current filters. Try adjusting your search criteria or add new items to monitor.</p>
            </div>
        );
    }

    return (
        <div className="relative font-mono">
            <Table
                columns={columns}
                onSort={onSort}
                sortColumn={sortField}
                sortDirection={sortDirection}
                selectable
                onSelectAll={onSelectAll}
                allRowsSelected={items.length > 0 && selectedItems.length === items.length}
                indeterminate={selectedItems.length > 0 && selectedItems.length < items.length}
                loading={isLoading}
                loadingRows={itemsPerPage}
            >
                {items.map((item) => (
                    <TableRow
                        key={item.id}
                        selected={selectedItems.includes(item.id)}
                        onSelect={(checked) => onSelectItem(item.id, checked)}
                        rowId={item.id.toString()}
                    >
                        <TableCell>
                            <EntryTypeBadge entryType={item.entry_type} />
                        </TableCell>
                        <TableCell>
                            <EntryDisplay entry={item.entry} entryType={item.entry_type} />
                        </TableCell>
                        <TableCell>
                            <StatusBadge status={item.status} />
                        </TableCell>
                        <TableCell>
                            <span className="font-mono text-[11px] text-slate-400">{item.notify_email}</span>
                        </TableCell>
                        <TableCell>
                            <DateDisplay date={item.added_at} />
                        </TableCell>
                        <TableCell>
                            <DropdownMenu>
                                <DropdownMenuTrigger className="text-slate-500 hover:text-white p-2 rounded transition-colors outline-none hover:bg-[#161A23]">
                                    <MoreVertical className="h-4 w-4" />
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="cyber-dropdown-menu">
                                    <DropdownMenuItem onClick={() => onToggleItem(item)} className="group">
                                        {item.status === 'enabled' ? (
                                            <>
                                                <EyeOff className="h-3.5 w-3.5 mr-2 text-slate-500 group-hover:text-orange-400" />
                                                Suspend Monitor
                                            </>
                                        ) : (
                                            <>
                                                <Eye className="h-3.5 w-3.5 mr-2 text-slate-500 group-hover:text-[#50A970]" />
                                                Engage Monitor
                                            </>
                                        )}
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator />
                                    <DropdownMenuItem onClick={() => onDeleteItem(item.id)} className="group" destructive>
                                        <Trash2 className="h-3.5 w-3.5 mr-2 text-slate-500 group-hover:text-[#FF3333]" />
                                        Purge Entity
                                    </DropdownMenuItem>
                                </DropdownMenuContent>
                            </DropdownMenu>
                        </TableCell>
                    </TableRow>
                ))}
            </Table>

            {/* Local Styles for Radix Dropdown specific to the Watchlist theme */}
            <style dangerouslySetInnerHTML={{
                __html: `
                [data-radix-popper-content-wrapper] > div[role="menu"],
                .cyber-dropdown-menu {
                    background-color: #0A0B0E !important;
                    border: 1px solid #1A1D24 !important;
                    font-family: monospace !important;
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.9) !important;
                    min-width: 180px !important;
                    padding: 6px !important;
                }

                div[role="menuitem"] {
                    color: #94a3b8 !important;
                    font-size: 11px !important;
                    text-transform: uppercase !important;
                    letter-spacing: 0.05em !important;
                    padding: 10px 14px !important;
                    cursor: pointer !important;
                    transition: all 0.1s ease !important;
                    border-radius: 4px !important;
                    display: flex !important;
                    align-items: center !important;
                    outline: none !important;
                }

                div[role="menuitem"]:hover,
                div[role="menuitem"][data-highlighted] {
                    background-color: #12151C !important;
                    color: #4da760 !important;
                }

                div[role="menuitem"][data-destructive="true"]:hover {
                    color: #FF3333 !important;
                    background-color: rgba(255, 51, 51, 0.1) !important;
                }

                div[role="separator"] {
                    background-color: #1A1D24 !important;
                    height: 1px !important;
                    margin: 4px 0 !important;
                }
            `}} />
        </div>
    );
};

export default WatchlistTable;